import React, { FC, useEffect, useContext } from 'react';
import { Autocomplete, Box, FormControl, TextField } from '@mui/material';
import { useQuery } from '@apollo/client';
import { getDeliveryClinicsTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { Organization } from 'models/organization';
import { ActionOrderContext } from 'pages/order-flow/action-order/action-order-context';

type DeliveryClinicPickerProps = {
    clinic?: Organization;
    onClinicSelected?: (clinic: Organization) => void;
    label: string;
    dataTest?: string;
};
const DeliveryClinicPicker: FC<DeliveryClinicPickerProps> = ({ clinic, onClinicSelected, label, dataTest }) => {
    const [selectedClinic, setSelectedClinic] = React.useState<Organization>();
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrganization = currentOrgVar();
    const { order } = useContext(ActionOrderContext);

    useEffect(() => {
        if (selectedClinic && onClinicSelected) {
            onClinicSelected(selectedClinic);
        }
    }, [selectedClinic]);

    const { data: { childClinicsWithParent } = {}, loading } = useQuery(getDeliveryClinicsTemplate, {
        variables: {
            parentClinicId: order?.customer?._id || currentOrganization?._id,
            showOnlyDeliveryLocations: true,
        },
        onCompleted({ childClinicsWithParent }) {
            if (!selectedClinic && childClinicsWithParent.length === 1) {
                setSelectedClinic(childClinicsWithParent[0]);
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (clinic && childClinicsWithParent?.find((c: Organization) => c._id === clinic._id)) {
            setSelectedClinic(clinic);
        }
    }, [clinic, childClinicsWithParent]);

    const handleClinicSelected = (clinic: Organization | null) => {
        if (clinic) {
            setSelectedClinic(clinic);
        }
    };

    return (
        <Box>
            {!loading && (
                <FormControl fullWidth margin="dense">
                    <Autocomplete
                        options={childClinicsWithParent}
                        getOptionLabel={(clinic: Organization) => `${clinic.name || ''}`}
                        isOptionEqualToValue={(option: Organization, value: Organization) => option?._id === value?._id}
                        value={(selectedClinic || { name: '' }) as Organization}
                        onChange={(_, value) => handleClinicSelected(value)}
                        renderInput={(params) => <TextField {...params} label={label} data-test={dataTest} />}
                    />
                </FormControl>
            )}
        </Box>
    );
};

export default DeliveryClinicPicker;
